<template>
    <BuyRequestsTableWrapper>
        <template #actions>
            <CreateButton
                v-if="userStore.can('create_buy_request')"
                @click.stop="openCreateBuyRequestModal"
            />
        </template>
        <template #filters>
            <DropdownFilter
                :pin="pinFilters"
                @apply="applyFilters"
                @clear="clearFilters"
                @toggle-pin="togglePinFilters"
            >
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.asset_uid"
                            v-model:options="assetSelectOptions"
                            name="asset_uid"
                            class="w-full"
                            :placeholder="$t('asset')"
                            :on-search="loadAssetSelectOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.type"
                            name="type"
                            class="w-full"
                            :options="typeOptions"
                            :placeholder="$t('type')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.status"
                            name="status"
                            class="w-full"
                            :options="statusOptions"
                            :placeholder="$t('status')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </BuyRequestsTableWrapper>

    <ModalWrapper v-if="isModalOpen" />

    <CreateBuyRequestModal
        v-if="createBuyRequestModalOpen"
        ref="createBuyRequestModal"
        v-model="tempBuyRequest"
        v-model:open="createBuyRequestModalOpen"
        :platform-name="initialFilters?.platform_name_exact"
        @submit="createBuyRequest"
        @cancel="closeCreateBuyRequestModal"
    />

    <DeleteItemModal
        v-if="tempBuyRequest"
        id="cancel-buy-request-modal"
        ref="cancelBuyRequestModal"
        v-model="tempBuyRequest"
        v-model:open="cancelBuyRequestModalOpen"
        title="modal_title_cancel_buy_request"
        name-key="id"
        id-key="id"
        @submit="cancelBuyRequest"
        @cancel="closeCancelBuyRequestModal"
    />
</template>

<script setup lang="ts">
import { useTable } from '@composables/common'
import {
    DeleteItemModal,
    DropdownFilter,
    DropdownFilterItem,
    SelectField,
} from '@src/components'
import { useModal } from '@src/composables/common'
import CreateButton from '@src/partials/actions/CreateButton.vue'
import { Asset, BuyRequest, TableSorting } from '@src/types'
import { getAssets } from '@src/utils/api/assets'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { onMounted, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmAssetType, SmBuyRequestStatusType } from '@enums/SecondaryMarket'
import CreateBuyRequestModal from '@partials/secondaryMarket/CreateBuyRequestModal.vue'
import { formatSecondaryMarketValue } from '@partials/secondaryMarket/formatSecondaryMarketValue'
import {
    getBuyRequests,
    cancelBuyRequest as apiCancelBuyRequest,
    createBuyRequest as apiCreateBuyRequest,
} from '@src/utils/api/secondaryMarket'

// Interface definition for Secondary Market Buy Request records
interface SecondaryMarketBuyRequest {
    id: string
    buyer_id: string
    buyer_title?: string
    buyer_email?: string
    buyer_account_type?: 'natural' | 'legal' | string
    buyer_profile_id?: string
    seller_id?: string
    seller_title?: string
    asset_id: string
    asset_title?: string
    asset?: {
        id: string
        asset_title?: string
        contract_template_url?: string
        external_url?: string
    }
    type: SmAssetType
    desired_quantity: number
    target_price_per_unit: number
    status: SmBuyRequestStatusType
    valid_until: string
    created_at: string
    note?: string
}

const props = withDefaults(
    defineProps<{
        initialFilters?: Record<string, any> | null
        createInitialValues?: SecondaryMarketBuyRequest | null
    }>(),
    {
        initialFilters: null,
        createInitialValues: null,
    }
)

const { createInitialValues } = toRefs(props)

const toast = useToastStore()
const i18n = useI18n()
const userStore = useUserStore()
const { ModalWrapper } = useModal()

const isModalOpen = ref(false)
const buyRequests = ref<BuyRequest[]>([])
const buyRequestsCount = ref<number>(0)
const assetSelectOptions = ref<{ label: string; value: string | null }[]>([])

const tempBuyRequest = ref<BuyRequest | null>(null)
const createBuyRequestModalOpen = ref(false)
const cancelBuyRequestModalOpen = ref(false)

// Filter options
const typeOptions = [
    { label: i18n.t('pre_emptive'), value: 'pre_emptive' },
    { label: i18n.t('share'), value: 'share' },
]

const statusOptions = [
    { label: i18n.t('status_open'), value: 'open' },
    { label: i18n.t('status_reserved'), value: 'reserved' },
    { label: i18n.t('status_sold'), value: 'sold' },
    { label: i18n.t('status_cancelled'), value: 'cancelled' },
    { label: i18n.t('status_expired'), value: 'expired' },
]

const {
    TableWrapper: BuyRequestsTableWrapper,
    selected,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    pinFilters,
    togglePinFilters,
    setFilters,
    applyFilters,
    clearFilters,
} = useTable({
    loadData: loadBuyRequests,
    filters: {
        asset_uid: null,
        type: null,
        status: null,
    },
    tableProps: {
        id: 'buy-requests-table',
        label: 'secondary_market_buy_requests',
        help: 'help_buy_requests_table',
        pagination: true,
        data: buyRequests,
        total: buyRequestsCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'id',
                key: 'id',
                condition: userStore.can('see_id_columns'),
                copy: true,
            },
            {
                label: 'asset',
                key: 'asset_title',
                url: (row: SecondaryMarketBuyRequest) => assetUrl(row),
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return (
                        props.rowData.asset_title ||
                        props.rowData.asset?.asset_title ||
                        props.rowData.asset_id ||
                        '-'
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'buyer',
                key: 'buyer_title',
                url: (row: SecondaryMarketBuyRequest) =>
                    accountUrl(row.buyer_profile_id, row.buyer_account_type),
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return (
                        props.rowData.buyer_title ||
                        props.rowData.buyer_email ||
                        props.rowData.buyer_id ||
                        '-'
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'type',
                key: 'type',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return props.rowData.type === SmAssetType.PRE_EMPTIVE
                        ? i18n.t('pre_emptive')
                        : i18n.t('share')
                },
            },
            {
                label: 'quantity',
                key: 'desired_quantity',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return formatSecondaryMarketValue(
                        props.rowData.desired_quantity
                    )
                },
            },
            {
                label: 'price_per_unit',
                key: 'target_price_per_unit',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return `€ ${formatSecondaryMarketValue(
                        props.rowData.target_price_per_unit
                    )}`
                },
            },
            {
                label: 'status',
                key: 'status',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return i18n.t(`status_${props.rowData.status}`)
                },
            },
            {
                label: 'valid_until',
                key: 'valid_until',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return props.rowData.valid_until
                        ? moment(props.rowData.valid_until).format(
                              'DD.MM.YYYY HH:mm'
                          )
                        : '-'
                },
            },
            {
                label: 'created_at',
                key: 'created_at',
                sorting: true,
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return moment(props.rowData.created_at).format(
                        'DD.MM.YYYY HH:mm'
                    )
                },
            },
            {
                label: 'note',
                key: 'note',
                cellRenderer: (props: {
                    rowData: SecondaryMarketBuyRequest
                }) => {
                    return props.rowData.note || '-'
                },
            },
        ],
        actions: [
            {
                action: handleCancelBuyRequest,
                icon: 'fas fa-trash-alt',
                title: 'cancel',
                color: 'text-danger-500',
                condition: (row: SecondaryMarketBuyRequest) =>
                    userStore.can('cancel_buy_request') &&
                    row.status === SmBuyRequestStatusType.OPEN,
            },
        ],
    },
})

// Load paginated buy request records
async function loadBuyRequests() {
    const { data } = await getBuyRequests(
        filters.value,
        page.value,
        limit.value,
        sorting.value
    )
    buyRequests.value = data.buy_requests
    buyRequestsCount.value = data.buy_requests_count
    selected.value = []
}

// Load assets dropdown options for filter
async function loadAssetSelectOptions(search: string) {
    const assetsRequest = await getAssets({
        'filter[asset_title]': search,
    })
    assetSelectOptions.value = assetsRequest.data.assets.map(
        (asset: Asset) => ({
            label: asset.asset_title as string,
            value: asset.asset_uid as string,
        })
    )
}

// Set target buy request and show cancellation modal
function handleCancelBuyRequest(buyRequest: SecondaryMarketBuyRequest) {
    tempBuyRequest.value = buyRequest
    cancelBuyRequestModalOpen.value = true
}

// Close cancellation modal
function closeCancelBuyRequestModal() {
    cancelBuyRequestModalOpen.value = false
    tempBuyRequest.value = null
}

// Execute cancellation API request
async function cancelBuyRequest(buyRequest: SecondaryMarketBuyRequest) {
    if (!buyRequest.id) {
        toast.error(i18n.t('toast_error_cannot_cancel_buy_request'))
        return
    }

    await apiCancelBuyRequest(buyRequest.id)

    cancelBuyRequestModalOpen.value = false
    tempBuyRequest.value = null

    toast.success(i18n.t('toast_success_buy_request_cancelled'))

    page.value = 1
    await loadBuyRequests()
}

function openCreateBuyRequestModal() {
    tempBuyRequest.value = {
        id: '',
        buyer_id: '',
        asset_id: '',
        type: SmAssetType.PRE_EMPTIVE,
        desired_quantity: 0,
        target_price_per_unit: 0,
        status: SmBuyRequestStatusType.OPEN,
        created_at: '',
        ...(createInitialValues.value || {}),
    } as BuyRequest

    createBuyRequestModalOpen.value = true
}

function closeCreateBuyRequestModal() {
    tempBuyRequest.value = null
    createBuyRequestModalOpen.value = false
}

// Generate Asset details URL
function assetUrl(row: SecondaryMarketBuyRequest) {
    const assetId = row.asset_id || row.asset?.id
    return assetId ? '/assets/' + assetId : '#'
}

// Generate Profile URL based on profile type
function accountUrl(profileId?: string, profileType?: string) {
    if (!profileId || !profileType) return '#'

    return `/${profileType}Profile/${profileId}`
}

async function createBuyRequest() {
    console.log('createBuyRequest...', tempBuyRequest.value)
    try {
        await apiCreateBuyRequest(tempBuyRequest.value)
        toast.success(i18n.t('Kaufanfrage wurde erfolgreich erstellt!'))
        createBuyRequestModalOpen.value = false
        tempBuyRequest.value = null
        await loadBuyRequests()
    } catch (error: any) {
        toast.error(i18n.t(error?.message || error))
    }
}

onMounted(async () => {
    if (props.initialFilters) {
        filtersFields.value = {
            ...filtersFields.value,
            ...props.initialFilters,
        }
        setFilters()
    }
    await loadBuyRequests()
})
</script>
